import { Stack, Switch, Typography } from '@mui/material';
import DialogContentText from '@mui/material/DialogContentText';
import { useFormik } from 'formik';
import React, { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import { useUser } from '@sophocles/app';
import CustomerAutocomplete from '@sophocles/components/CustomerAutocomplete';
import FormErrorAlert from '@sophocles/components/FormErrorAlert';
import InventoryLocationAutocomplete from '@sophocles/components/InventoryLocationAutocomplete';
import ResponsiveDialog from '@sophocles/components/ResponsiveDialog';
import SiteGroupAutocomplete from '@sophocles/components/SiteGroupAutocomplete';
import { patchRadioBilling } from '@sophocles/entities/RadioBilling';
import { editRadioLocation } from '@sophocles/features/inventory/radioLocations/api';
import { addToast } from '@sophocles/features/toast';
import { applyApiErrorToFormik } from '@sophocles/utils/apiErrors';

// The form's Formik values (customerId/siteGroupId/inventoryLocationId) are
// camelCase UI state, not the flat snake_case payload the API validates
// against — no field name lines up with an input, so every message
// surfaces in the banner instead of inline.
const KNOWN_FIELDS = [];

function MoveInventoryDialog({
  open,
  customerId,
  siteGroupId = null,
  radioLocationId,
  radioBillingId,
  inventoryLocationId,
  targetSiteGroups = true,
  eventMessage,
  onSubmit,
  onClose,
  ...props
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isSiteGroupTarget, setIsSiteGroupTarget] = useState(targetSiteGroups);
  const [initialValues, setInitialValues] = useState({ customerId, siteGroupId, inventoryLocationId });
  const [nonFieldMessages, setNonFieldMessages] = useState([]);

  const { user } = useUser();
  let locationEvent = eventMessage;
  if (!locationEvent) {
    locationEvent = `Moved by ${user.get('first_name', 'Unknown')} ${user.get('last_name', 'Unknown')} (ID: ${user.get(
      'id',
      'N/A',
    )})`;
  }

  const moveInventoryItem = (siteGroupId, locationId) => {
    // Move radio location to new site group.
    return dispatch(
      editRadioLocation({
        id: radioLocationId,
        site_group: siteGroupId,
        inventory_location: locationId,
        event: locationEvent,
      }),
    )
      .unwrap()
      .then(() => {
        if (radioBillingId) {
          // Update billing to new site group.
          dispatch(patchRadioBilling(radioBillingId, { billed_site_group_id: siteGroupId }));
        }
      })
      .then(() => {
        dispatch(
          addToast({
            title: t('general.success'),
            message: t('inventory.moveInventorySuccess'),
            level: 'success',
            dismissible: true,
          }),
        );
      });
  };

  useEffect(() => {
    setIsSiteGroupTarget(targetSiteGroups);
  }, [targetSiteGroups]);

  useEffect(() => {
    setInitialValues({ customerId, siteGroupId, inventoryLocationId });
  }, [isSiteGroupTarget]);

  useEffect(() => {
    if (open) {
      setNonFieldMessages([]);
    }
  }, [open]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: yup.object({
      customerId: yup.number().nullable(),
      siteGroupId: yup.number().nullable(),
      inventoryLocationId: yup.number().nullable(),
    }),
    onSubmit: values => {
      // eslint-disable-next-line no-use-before-define -- formik <-> handleSubmit form a submit-time cycle
      handleSubmit(values);
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const handleSubmit = values => {
    setNonFieldMessages([]);
    moveInventoryItem(values.siteGroupId, values.inventoryLocationId)
      .then(() => {
        if (onSubmit) {
          onSubmit(values);
        }

        handleClose();
      })
      .catch(error => {
        setNonFieldMessages(
          applyApiErrorToFormik(error, {
            setErrors: formik.setErrors,
            knownFields: KNOWN_FIELDS,
            genericMessage: t('inventory.moveInventoryFailed'),
          }),
        );
      });
  };

  return (
    <ResponsiveDialog
      title={t('inventory.moveInventory')}
      open={open}
      canSubmit={formik.values.siteGroupId != null || formik.values.inventoryLocationId != null}
      onClose={handleClose}
      PaperProps={{ sx: { minHeight: '80%' } }}
      onSubmit={() => {
        formik.submitForm();
      }}
      {...props}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography>{t('general.siteGroup')}</Typography>
        <Switch checked={!isSiteGroupTarget} onChange={() => setIsSiteGroupTarget(!isSiteGroupTarget)} />
        <Typography>{t('general.location')}</Typography>
      </Stack>
      <DialogContentText sx={{ my: 2 }}>{t('inventory.moveInventoryDetails')}</DialogContentText>
      <FormErrorAlert messages={nonFieldMessages} sx={{ mb: 2 }} />
      <Stack spacing={2}>
        {isSiteGroupTarget ? (
          <Fragment>
            <CustomerAutocomplete
              required
              fullWidth
              name="customerId"
              value={formik.values.customerId || ''}
              onChange={(e, newCustomerId) => {
                formik.handleChange(e);
              }}
            />
            <SiteGroupAutocomplete
              required
              fullWidth
              name="siteGroupId"
              disabled={formik.values.customerId == null}
              customerId={formik.values.customerId}
              value={formik.values.siteGroupId}
              onChange={(e, newSiteGroupId) => {
                formik.handleChange(e);
              }}
            />
          </Fragment>
        ) : (
          <InventoryLocationAutocomplete
            fullWidth
            required
            name="inventoryLocationId"
            error={formik.errors.inventoryLocationId != null}
            value={formik.values.inventoryLocationId || ''}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        )}
      </Stack>
    </ResponsiveDialog>
  );
}

export default MoveInventoryDialog;
